import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  GridReadyEvent,
  IRowNode,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { AgGridReact } from "ag-grid-react";
import React, { useCallback } from "react";
import { QUEUE_TYPE } from "../../constants/appointments";
import useDebounce from "../../hooks/use-debounce";
import useBillingStore from "../../stores/billing-store/billing-store";
import {
  paidStatusArray,
  pendingStatusArray,
  unbilledStatusArray,
} from "../../stores/billing-store/utils";
import "../../styles/billing-table.scss";
import { TReceiptEntity } from "../../types/billing";
import { getFilteredReceiptsByNameAndPhone } from "../../utils/billing-utils";
import { formatMutltipleReceiptToSeperateRow } from "../../utils/download-report-utils";
import EmptyStateRenderer from "./empty-queue";
import useGetAllAppointmentsV2 from "../../payment-dashboard-v2/api/useGetAllAppointmentsV2";

function ShimmerCell() {
  return (
    <div className="w-full h-full py-8 flex">
      <div className="w-full h-32 shimmer items-center"></div>
    </div>
  );
}

export default function BillingTable() {
  const {
    clinicId,
    receiptEntities,
    queueColumnDefinitions,
    setQueueColumnDefinitions,
    setReceiptEntities,
    paymentTimelineDuration,
    paymentGroupFilter,
    searchFilterString,
    paymodeFilters,
    serviceFilters,
    setLastUpdatedAt,
  } = useBillingStore();

  const onColumnResize = React.useCallback(
    (props: ColumnResizedEvent<TReceiptEntity, any>) => {
      const { source } = props;
      if (source === "sizeColumnsToFit") {
        const { columns } = props;
        const newDefinitions =
          columns?.map((column) => {
            return { width: column.getActualWidth() };
          }) || [];

        setQueueColumnDefinitions(
          queueColumnDefinitions?.map((currentColDef, index) => {
            const currColWidth = newDefinitions[index]?.width;
            if (currColWidth) {
              return { ...currentColDef, width: currColWidth };
            }
            return currentColDef;
          })
        );
      } else {
        const currentActualWidth = props.column?.getActualWidth();
        const updatedColumnColId = props.column?.getColId();
        setQueueColumnDefinitions(
          queueColumnDefinitions?.map((currentColDef) => {
            const colDef = currentColDef as ColDef<TReceiptEntity>;
            const { colId } = colDef;
            if (colId === updatedColumnColId) {
              return { ...currentColDef, width: currentActualWidth };
            }
            return currentColDef;
          })
        );
      }
    },
    [queueColumnDefinitions, setQueueColumnDefinitions]
  );

  const debouncedOnColumnResize = useDebounce(() => onColumnResize, 300);

  const onColumnMove = React.useCallback(
    (props: ColumnMovedEvent<TReceiptEntity, any>) => {
      if (props.finished) {
        const { toIndex, column } = props;
        const updatedColumnColId = column?.getColId();
        if (updatedColumnColId) {
          let toBeReAddedColumn: ColDef<TReceiptEntity> | undefined;
          const updatedQueueColDefs = queueColumnDefinitions?.filter(
            (currentQueueColDef) => {
              const colDef = currentQueueColDef as ColDef<TReceiptEntity>;
              if (colDef.colId !== updatedColumnColId) {
                return true;
              } else {
                toBeReAddedColumn = currentQueueColDef;
              }
            }
          );
          if (toIndex && toBeReAddedColumn) {
            updatedQueueColDefs?.splice(toIndex, 0, toBeReAddedColumn);
            setQueueColumnDefinitions(updatedQueueColDefs);
          }
        }
      }
    },
    [queueColumnDefinitions, setQueueColumnDefinitions]
  );

  const debouncedOnColumnMove = useDebounce(() => onColumnMove, 300);

  const gridRef = React.useRef<AgGridReact<TReceiptEntity> | null>(null);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    const { columnApi } = params;

    // Ignoring ts here because columnModel is kept as a private type in columnApi and cannot be accessed directly. The getter functions are not accessible via typescript

    // @ts-ignore
    const bodyWidth = columnApi.columnModel.bodyWidth;
    // @ts-ignore
    const scrollWidth = columnApi.columnModel.scrollWidth;

    if (bodyWidth < scrollWidth) {
      params.api.sizeColumnsToFit();
    }
  }, []);

  const { data: billingQueue, isLoading } = useGetAllAppointmentsV2({
    clinicId,
    startDate: paymentTimelineDuration.startDate,
    endDate: paymentTimelineDuration.endDate,
  });

  const { response } = billingQueue || {};
  const { apt: apts = [] } = response || {};

  React.useEffect(() => {
    if (!isLoading) {
      const receiptEntitiesCreated: TReceiptEntity[] =
        formatMutltipleReceiptToSeperateRow(apts);
      setReceiptEntities({ receiptEntities: receiptEntitiesCreated });
      setLastUpdatedAt(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apts, isLoading, paymentGroupFilter]);

  React.useEffect(() => {
    gridRef?.current?.api?.onFilterChanged();
    const currentNodesShown = gridRef.current?.api?.getRenderedNodes();

    if (currentNodesShown && currentNodesShown.length < 1) {
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, [paymentGroupFilter, searchFilterString, paymodeFilters, serviceFilters]);

  const loadingGridRef = React.useRef<AgGridReact<boolean> | null>(null);

  if (isLoading) {
    return (
      <div className="ag-theme-alpine w-full h-full font-lato">
        <AgGridReact
          ref={loadingGridRef}
          headerHeight={40}
          rowHeight={60}
          rowData={[false, false, false, false]}
          suppressDragLeaveHidesColumns={true}
          columnDefs={queueColumnDefinitions?.map(
            ({
              colId,
              hide,
              headerName,
              width,
              headerClass,
              pinned,
              maxWidth,
              minWidth,
              resizable,
            }: ColDef) => {
              return {
                colId,
                hide,
                headerName,
                width,
                headerClass,
                cellRenderer: ShimmerCell,
                pinned,
                maxWidth,
                minWidth,
                resizable,
              };
            }
          )}
        />
      </div>
    );
  }

  return (
    <div className="ag-theme-alpine w-full h-full font-lato">
      <AgGridReact
        ref={gridRef}
        headerHeight={40}
        rowHeight={47}
        rowData={receiptEntities}
        columnDefs={queueColumnDefinitions}
        groupAggFiltering={true}
        suppressCellFocus={true}
        onColumnMoved={debouncedOnColumnMove}
        onColumnResized={debouncedOnColumnResize}
        onGridReady={onGridReady}
        onCellEditingStopped={(props) => {
          const updatedAmount = props.newValue;
          if (updatedAmount) {
            const updatedAmountInNumber = parseFloat(updatedAmount);
            setReceiptEntities({
              receiptEntities: receiptEntities.map((item) => {
                if (item.idx === props.data.idx) {
                  return { ...item, amount: updatedAmountInNumber };
                } else {
                  return item;
                }
              }),
            });
          }
        }}
        getRowId={(rowEntity) => {
          const { data } = rowEntity;
          const { aid, receipt_id } = data;

          return `${aid}${receipt_id ? `_${receipt_id}` : ""}`;
        }}
        suppressRowTransform={true}
        suppressDragLeaveHidesColumns={true}
        isExternalFilterPresent={() => true}
        onSortChanged={(sortEvent) => {
          sortEvent.api.refreshCells({ columns: ["idx"], force: true });
        }}
        onFilterChanged={(filterEvent) => {
          filterEvent.api.refreshCells({ columns: ["idx"], force: true });
        }}
        doesExternalFilterPass={(node: IRowNode<TReceiptEntity>) => {
          let matchesPaymodeFilter = false,
            matchesServiceFitler = false,
            matchesSearchStringFilter = false,
            matchesPaymentGroupFilter = false;
          const { data: receiptRowEntity } = node;

          matchesSearchStringFilter = !!getFilteredReceiptsByNameAndPhone({
            receipts: receiptRowEntity ? [receiptRowEntity] : [],
            nameOrPhoneorUHIDString: searchFilterString || "",
          }).length;

          if (!receiptRowEntity) return false;

          const { paymentItems, receiptSkus } = receiptRowEntity;

          switch (paymentGroupFilter) {
            case "all-receipts": {
              matchesPaymentGroupFilter = true;
              break;
            }
            case "paid": {
              matchesPaymentGroupFilter = paidStatusArray.includes(
                receiptRowEntity.payment_status
              );
              break;
            }
            case "pending-payment": {
              matchesPaymentGroupFilter = pendingStatusArray.includes(
                receiptRowEntity.payment_status
              );
              break;
            }
            case "unbilled": {
              matchesPaymentGroupFilter = unbilledStatusArray.includes(
                receiptRowEntity.payment_status
              );
              break;
            }
            default: {
              matchesPaymentGroupFilter = true;
            }
          }

          if (!paymodeFilters.length) {
            matchesPaymodeFilter = true;
          }
          for (let i = 0; i < paymentItems.length; i++) {
            const payItem = paymentItems[i];
            if (paymodeFilters.includes(payItem.paymode)) {
              matchesPaymodeFilter = true;
              break;
            }
          }

          if (!serviceFilters.length) {
            matchesServiceFitler = true;
          }
          for (let i = 0; i < receiptSkus.length; i++) {
            const skuItem = receiptSkus[i];
            if (
              serviceFilters.includes(skuItem.sku_item.service_id as number)
            ) {
              matchesServiceFitler = true;
              break;
            }
          }

          return (
            matchesPaymodeFilter &&
            matchesServiceFitler &&
            matchesSearchStringFilter &&
            matchesPaymentGroupFilter
          );
        }}
        noRowsOverlayComponent={EmptyStateRenderer}
      />
    </div>
  );
}
